import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import PdfViewer from "../components/PdfViewer";
import { useTheme } from "../context/ThemeContext";

const TINTS = [
  { bg: "#FFF1B8", shadow: "rgba(200, 160, 60, 0.18)", tape: "#F4D08C" },
  { bg: "#FFD9C4", shadow: "rgba(200, 110, 80, 0.18)", tape: "#F2A88E" },
  { bg: "#D9F0D4", shadow: "rgba(80, 150, 90, 0.18)",  tape: "#A6D6A0" },
  { bg: "#E5D9F2", shadow: "rgba(130, 110, 180, 0.18)", tape: "#BDA8DC" },
  { bg: "#FFD6E0", shadow: "rgba(210, 110, 140, 0.18)", tape: "#F2A8B8" },
  { bg: "#CFE7F2", shadow: "rgba(80, 140, 180, 0.18)",  tape: "#9CC6DC" },
];
const DARK_TINTS = [
  { bg: "#3D3520", shadow: "rgba(0,0,0,0.4)", tape: "#7A6A40" },
  { bg: "#3D2820", shadow: "rgba(0,0,0,0.4)", tape: "#7A4D38" },
  { bg: "#1F3328", shadow: "rgba(0,0,0,0.4)", tape: "#3D6650" },
  { bg: "#2A2440", shadow: "rgba(0,0,0,0.4)", tape: "#544870" },
  { bg: "#3D2030", shadow: "rgba(0,0,0,0.4)", tape: "#704050" },
  { bg: "#1F2A38", shadow: "rgba(0,0,0,0.4)", tape: "#385570" },
];

export default function NoteDetail() {
  const { slug } = useParams();
  const [post, setPost]         = useState(null);
  const [allPosts, setAllPosts] = useState([]);
  const [pdfPages, setPdfPages] = useState(null);
  const { dark } = useTheme();

  // Scroll to top + clear stale page count whenever the note changes.
  // This fixes the "Next in Subject" navigation bug where the user was
  // scrolled to the bottom of the previous note and couldn't see the new content.
  useEffect(() => {
    window.scrollTo(0, 0);
    setPdfPages(null);
    fetch(`/api/posts/${slug}`)
      .then((r) => r.json())
      .then(setPost)
      .catch(() => {});
  }, [slug]);

  // Load full list once — used only to find the next note in the same subject.
  useEffect(() => {
    fetch("/api/posts")
      .then((r) => r.json())
      .then(setAllPosts)
      .catch(() => {});
  }, []);

  const nextInSubject = useMemo(() => {
    if (!post || !allPosts.length) return null;
    const same = allPosts.filter(
      (p) => (p.subject || "General") === (post.subject || "General")
    );
    const idx = same.findIndex((p) => p.slug === post.slug);
    if (idx === -1 || same.length < 2) return null;
    // Wrap around: if at the last note, go back to the first
    return same[idx + 1] ?? same[0];
  }, [post, allPosts]);

  if (!post) return null;

  const tints    = dark ? DARK_TINTS : TINTS;
  const accent   = tints[1]; // peach
  const timeTint = tints[2]; // mint
  const nextTint = tints[3]; // lavender

  // Time to read: 5 minutes per PDF page.
  // Shows "…" while the PDF is still loading.
  const readLabel = pdfPages != null
    ? `~${pdfPages * 5} min`
    : "…";
  const pageLabel = pdfPages != null
    ? `${pdfPages} page${pdfPages === 1 ? "" : "s"}`
    : "";

  const ldJson = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.summary,
    author: { "@type": "Person", name: "Farjana Yasmin Mim" },
    publisher: {
      "@type": "Organization",
      name: "5 Minute Nursing School",
      url: "https://5minutenursingschool.com",
    },
    image: `https://5minutenursingschool.com/api/posts/${post.slug}/cover`,
    datePublished: post.published_at,
    mainEntityOfPage: `https://5minutenursingschool.com/study/${post.slug}`,
  };

  return (
    <div
      className="min-h-screen transition-colors duration-300 font-[Quicksand]"
      style={{ background: dark ? "#1A1612" : "#FFF8EC" }}
    >
      <title>{`${post.title} | 5 Minute Nursing School`}</title>
      <meta name="description" content={post.summary} />
      <link rel="canonical" href={`https://5minutenursingschool.com/study/${post.slug}`} />
      <meta property="og:title" content={`${post.title} | 5 Minute Nursing School`} />
      <meta property="og:description" content={post.summary} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={`https://5minutenursingschool.com/study/${post.slug}`} />
      <meta property="og:image" content={`https://5minutenursingschool.com/api/posts/${post.slug}/cover`} />
      <script type="application/ld+json">{JSON.stringify(ldJson)}</script>

      <div className="max-w-6xl mx-auto p-6 grid gap-7 grid-cols-1 lg:grid-cols-[1.6fr_1fr]">

        {/* ── PDF panel ── */}
        <div
          className="relative rounded-md p-5"
          style={{
            background: dark ? "#1F1A14" : "#fff",
            boxShadow: dark
              ? "0 12px 28px rgba(0,0,0,0.5)"
              : "0 12px 28px rgba(180,140,90,0.14)",
          }}
        >
          {/* dual washi tape */}
          <span className="absolute" style={{ top: -10, left: "18%",  transform: "rotate(-4deg)", width: 80, height: 22, background: tints[0].tape, opacity: 0.75, borderRadius: 1 }} />
          <span className="absolute" style={{ top: -10, right: "18%", transform: "rotate(3deg)",  width: 80, height: 22, background: tints[2].tape, opacity: 0.75, borderRadius: 1 }} />

          <PdfViewer
            src={`/api/posts/${post.slug}/pdf`}
            onPagesLoaded={setPdfPages}
          />
        </div>

        {/* ── Sidebar ── */}
        <aside className="grid gap-4 content-start">

          {/* Summary tile (peach, slight tilt) */}
          <div
            className="relative p-6"
            style={{
              background: accent.bg,
              borderRadius: 6,
              transform: "rotate(0.8deg)",
              boxShadow: `0 12px 28px ${accent.shadow}`,
            }}
          >
            <span className="absolute" style={{ top: -12, left: "50%", transform: "translateX(-50%) rotate(-2deg)", width: 90, height: 22, background: accent.tape, opacity: 0.7 }} />
            <div
              className="inline-block px-3 py-1 rounded-full text-[11px] font-bold tracking-[0.16em] mb-3.5"
              style={{ background: dark ? "#1F1A14" : "#fff", color: dark ? "#F2A88E" : "#A04030" }}
            >
              {(post.subject || "GENERAL").toUpperCase()}
            </div>
            <h1 className="text-2xl font-bold leading-snug m-0" style={{ color: dark ? "#F5E8D0" : "#3D2820" }}>
              {post.title}
            </h1>
            <div className="text-[13px] mt-2 font-semibold" style={{ color: dark ? "#F2A88E" : "#A04030" }}>
              {new Date(post.published_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
            </div>
            <div className="h-0.5 my-4 rounded-sm" style={{ background: "rgba(255,255,255,0.5)" }} />
            <p className="text-sm leading-relaxed m-0 whitespace-pre-line" style={{ color: dark ? "#F5E8D0" : "#4A2820" }}>
              {post.summary}
            </p>
          </div>

          {/* Time to read tile (mint, slight counter-tilt) */}
          <div
            className="relative px-5 py-4"
            style={{
              background: timeTint.bg,
              borderRadius: 6,
              transform: "rotate(-0.6deg)",
              boxShadow: `0 8px 20px ${timeTint.shadow}`,
            }}
          >
            <span className="absolute" style={{ top: -8, right: 24, width: 50, height: 16, background: timeTint.tape, opacity: 0.7, transform: "rotate(4deg)" }} />
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[11px] font-bold tracking-[0.14em] mb-1" style={{ color: dark ? "#A6D6A0" : "#3D5028" }}>
                  TIME TO READ
                </div>
                <div className="text-2xl font-bold" style={{ color: dark ? "#F5E8D0" : "#2D4020" }}>
                  {readLabel}
                </div>
                {pageLabel && (
                  <div className="text-[11px] mt-0.5" style={{ color: dark ? "#A6D6A0" : "#5A7040" }}>
                    {pageLabel}
                  </div>
                )}
              </div>
              <div className="text-4xl leading-none">☕</div>
            </div>
          </div>

          {/* Next in subject tile (lavender) — hover handled via React state */}
          {nextInSubject && (
            <NextTile post={nextInSubject} subject={post.subject} tint={nextTint} dark={dark} />
          )}
        </aside>
      </div>
    </div>
  );
}

function NextTile({ post: next, subject, tint, dark }) {
  const [hovered, setHovered] = useState(false);
  return (
    <Link
      to={`/study/${next.slug}`}
      className="block relative px-5 py-4 no-underline"
      style={{
        background: tint.bg,
        borderRadius: 6,
        transform: hovered ? "rotate(0deg) translateY(-2px)" : "rotate(0.5deg)",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        boxShadow: hovered
          ? `0 14px 28px ${tint.shadow}`
          : `0 8px 20px ${tint.shadow}`,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span className="absolute" style={{ top: -8, left: 24, width: 50, height: 16, background: tint.tape, opacity: 0.7, transform: "rotate(-3deg)" }} />
      <div className="text-[11px] font-bold tracking-[0.14em] mb-2" style={{ color: dark ? "#BDA8DC" : "#4A3060" }}>
        NEXT IN {(subject || "GENERAL").toUpperCase()}
      </div>
      <div className="text-[15px] font-bold leading-snug" style={{ color: dark ? "#F5E8D0" : "#2D1D40" }}>
        {next.title} →
      </div>
    </Link>
  );
}
